import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getWeekPlans, createWeekPlan, getWeekReviews, extractErrorMessage } from '../services/api';
import { FaPlus, FaCalendarWeek, FaClipboardList } from 'react-icons/fa';
import { useToast } from '../components/Toast';
import UserSelect from '../components/UserSelect';

const DAYS_OF_WEEK = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const DAY_VALUES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function WeekPlanning({ user }) {
  const toast = useToast();
  const [plans, setPlans] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('plans');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [newPlan, setNewPlan] = useState({
    user_id: '',
    week_start: '',
    day_of_week: 'monday',
    schedule: '',
    tasks: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansData, reviewsData] = await Promise.all([
        getWeekPlans(),
        getWeekReviews().catch(() => [])
      ]);
      setPlans(plansData.plans || plansData || []);
      setReviews(reviewsData.reviews || reviewsData || []);
    } catch (error) {
      console.error('Error fetching week planning data:', error);
      toast.error(extractErrorMessage(error, 'Wochenplanungsdaten konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    try {
      await createWeekPlan(newPlan);
      setNewPlan({ user_id: '', week_start: '', day_of_week: 'monday', schedule: '', tasks: '', notes: '' });
      setShowModal(false);
      toast.success('Wochenplan-Eintrag erstellt');
      fetchData();
    } catch (error) {
      console.error('Error creating week plan:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Erstellen des Wochenplans'));
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError(null);
  };

  const getDayLabel = (day) => {
    const idx = DAY_VALUES.indexOf(day);
    return idx >= 0 ? DAYS_OF_WEEK[idx] : day;
  };

  const groupByWeek = (plansList) => {
    const grouped = {};
    plansList.forEach(plan => {
      const key = plan.week_start || 'unknown';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(plan);
    });
    return grouped;
  };

  const weekGroups = groupByWeek(plans);

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1><FaCalendarWeek /> Wochenplanung</h1>
          <button className="add-btn" onClick={() => setShowModal(true)}>
            <FaPlus /> Neuer Eintrag
          </button>
        </div>

        <div className="tab-bar">
          <button className={`tab-btn ${activeTab === 'plans' ? 'active' : ''}`} onClick={() => setActiveTab('plans')}>
            <FaCalendarWeek /> Wochenpläne
          </button>
          <button className={`tab-btn ${activeTab === 'reviews' ? 'active' : ''}`} onClick={() => setActiveTab('reviews')}>
            <FaClipboardList /> Wochenrückblicke
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : activeTab === 'plans' ? (
          <div className="data-table-container">
            {Object.keys(weekGroups).length > 0 ? (
              Object.entries(weekGroups).sort(([a], [b]) => b.localeCompare(a)).map(([weekStart, weekPlans]) => (
                <div key={weekStart} style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: '#fff', marginBottom: '12px' }}>
                    Woche ab {weekStart !== 'unknown' ? new Date(weekStart).toLocaleDateString('de-DE') : 'Unbekannt'}
                  </h3>
                  <div className="week-grid">
                    {DAY_VALUES.map((day, idx) => {
                      const dayPlan = weekPlans.find(p => p.day_of_week === day);
                      return (
                        <div key={day} style={{
                          background: '#2f3136',
                          borderRadius: '8px',
                          padding: '12px',
                          minHeight: '120px'
                        }}>
                          <h4 style={{ color: '#7289da', marginBottom: '8px', fontSize: '0.85rem' }}>
                            {DAYS_OF_WEEK[idx]}
                          </h4>
                          {dayPlan ? (
                            <>
                              {dayPlan.schedule && (
                                <p style={{ color: '#dcddde', fontSize: '0.8rem', marginBottom: '4px' }}>
                                  {dayPlan.schedule}
                                </p>
                              )}
                              {dayPlan.tasks && (
                                <p style={{ color: '#b9bbbe', fontSize: '0.75rem', marginBottom: '4px' }}>
                                  {dayPlan.tasks}
                                </p>
                              )}
                              {dayPlan.notes && (
                                <p style={{ color: '#72767d', fontSize: '0.7rem', fontStyle: 'italic' }}>
                                  {dayPlan.notes}
                                </p>
                              )}
                            </>
                          ) : (
                            <p style={{ color: '#72767d', fontSize: '0.75rem' }}>Kein Plan</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-data">Keine Wochenpläne vorhanden</div>
            )}
          </div>
        ) : (
          <div className="data-table-container">
            {reviews.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Woche</th>
                    <th>Benutzer</th>
                    <th>Zusammenfassung</th>
                    <th>Bewertung</th>
                    <th>Erstellt am</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.map(review => (
                    <tr key={review.id}>
                      <td>{review.week_start ? new Date(review.week_start).toLocaleDateString('de-DE') : '-'}</td>
                      <td>{review.username || review.user_id}</td>
                      <td>{review.summary || review.notes || '-'}</td>
                      <td>{review.rating ? `${review.rating}/10` : '-'}</td>
                      <td>{new Date(review.created_at || review.timestamp).toLocaleDateString('de-DE')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data">Keine Wochenrückblicke vorhanden</div>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neuer Wochenplan-Eintrag</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <UserSelect
                    value={newPlan.user_id}
                    onChange={id => setNewPlan({ ...newPlan, user_id: id })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Wochenbeginn *</label>
                  <input
                    type="date"
                    value={newPlan.week_start}
                    onChange={e => setNewPlan({ ...newPlan, week_start: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Wochentag *</label>
                  <select
                    value={newPlan.day_of_week}
                    onChange={e => setNewPlan({ ...newPlan, day_of_week: e.target.value })}
                    required
                  >
                    {DAY_VALUES.map((day, idx) => (
                      <option key={day} value={day}>{DAYS_OF_WEEK[idx]}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Zeitplan</label>
                  <textarea
                    value={newPlan.schedule}
                    onChange={e => setNewPlan({ ...newPlan, schedule: e.target.value })}
                    placeholder="Tagesablauf..."
                  />
                </div>
                <div className="form-group">
                  <label>Aufgaben</label>
                  <textarea
                    value={newPlan.tasks}
                    onChange={e => setNewPlan({ ...newPlan, tasks: e.target.value })}
                    placeholder="Aufgaben für den Tag..."
                  />
                </div>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={newPlan.notes}
                    onChange={e => setNewPlan({ ...newPlan, notes: e.target.value })}
                    placeholder="Zusätzliche Notizen..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal} disabled={saving}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={saving}>
                    {saving ? 'Speichert...' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default WeekPlanning;
